import { DocumentType, Invoice, InvoicePayment, InvoiceStatus } from '../types';
import { Cents, sumCents } from './money';
import {
    daysPastDue,
    effectiveInvoiceStatus,
    openAmountCents,
    overpaymentCents,
} from './invoiceLogic';
import { NEGATIVE_DOCUMENT_TYPES } from './creditNoteLogic';
import { DateWindow, QuotePeriod, periodWindow } from './quoteStats';

/**
 * Kennzahlen fuer das Rechnungs-Dashboard.
 *
 * Pure Funktionen wie quoteStats.ts und invoiceLogic.ts: kein Supabase, kein
 * Date.now(). "heute" wird immer hereingereicht — ohne das waere im Projekt
 * nichts davon pruefbar, weil es keine DOM-Testumgebung gibt.
 *
 * Zwei Sorten Kennzahlen, und die Trennung ist der ganze Punkt dieses Moduls:
 *
 *   - STICHTAGSGROESSEN beschreiben den BESTAND und gelten immer fuer heute:
 *     offene Forderungen, davon ueberfaellig, Entwuerfe, Belege in Pruefung,
 *     Kundenguthaben. Sie ignorieren jeden Zeitraumfilter. Eine Forderung von
 *     2024, die heute noch offen ist, ist heute offen — auch wenn der Filter
 *     auf "letzte 90 Tage" steht. Wer sie ins Fenster zwingt, zeigt eine
 *     Forderungssumme an, die kleiner ist als das, was tatsaechlich aussteht.
 *
 *   - FLUSSGROESSEN beschreiben einen ZEITRAUM und zaehlen nur Belege, deren
 *     RECHNUNGSDATUM im Fenster liegt: Umsatz, vereinnahmt, Gutschriften,
 *     Storno-Quote, DSO, Zahlungsmoral.
 *
 * Alle Geldgroessen sind ganzzahlige Cent (siehe money.ts).
 */

// ═══════════════════════════════════════════════════════════════════════════
// Typen
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Fenster und Voreinstellungen kommen aus quoteStats. Bewusst geteilt statt
 * nachgebaut: "letzte 90 Tage" muss im Angebots- und im Rechnungs-Dashboard
 * denselben Zeitraum bedeuten, sonst erklaert niemand die Differenz.
 */
export type { DateWindow };
export type InvoicePeriod = QuotePeriod;

export const invoicePeriodWindow = (period: InvoicePeriod, todayIso: string): DateWindow =>
    periodWindow(period, todayIso);

export interface InvoiceStats {
    // ── Stichtag (heute, unabhaengig vom Fenster) ───────────────────────────
    /** Summe der noch offenen Forderungen ueber alle fordernden Belege. */
    openCents: Cents;
    openCount: number;
    /** Teilmenge von openCents: Belege, deren Faelligkeit verstrichen ist. */
    overdueCents: Cents;
    overdueCount: number;
    draftCount: number;
    /** Belege im Status READY_FOR_REVIEW — warten auf die fachliche Freigabe. */
    inReviewCount: number;
    /** Ueberzahlungen, die noch auf ihrem Beleg stehen. */
    customerCreditCents: Cents;

    // ── Zeitraum (Rechnungsdatum im Fenster) ────────────────────────────────
    /** Ausgestellte Belege im Fenster, Korrekturbelege eingeschlossen. */
    issuedCount: number;
    /** Nettoumsatz. Vorzeichenlogik siehe revenueNetCents(). */
    revenueNetCents: Cents;
    /** Tatsaechlich eingegangenes Geld auf die Belege dieses Fensters (brutto). */
    collectedCents: Cents;
    creditedCount: number;
    /** Betrag der Gutschriften/Stornos, POSITIV ausgewiesen. */
    creditedNetCents: Cents;
    /** Anteil des gutgeschriebenen am ausgestellten Volumen, in Prozent. */
    cancellationRatePct: number | null;
    /** Ø Tage von Rechnungsdatum bis zur vollstaendigen Bezahlung. */
    dsoDays: number | null;
    /** Ø Tage ueber der Faelligkeit. Negativ = im Schnitt vorzeitig gezahlt. */
    avgPaymentDelayDays: number | null;

    // ── Laufender Kalendermonat (verankert an `today`) ──────────────────────
    /**
     * Der Monat, auf den sich die beiden folgenden Werte beziehen. Format
     * YYYY-MM, abgeleitet aus `today` und NICHT aus dem Fenster: die Frage
     * "was habe ich diesen Monat gestellt" meint immer den laufenden
     * Kalendermonat, egal welcher Zeitraum gerade eingestellt ist.
     */
    currentMonth: string;
    /**
     * Was im laufenden Monat an Forderungen ausgestellt wurde — BRUTTO und
     * nach Abzug der Abschlaege (issuedClaimCents = gross − prepaid). Bewusst
     * nicht der Nettoumsatz: die Zahl steht neben den offenen Forderungen, und
     * die sind ebenfalls brutto — zwei verschiedene Bezugsgroessen nebeneinander
     * liest niemand richtig.
     *
     * Und bewusst nicht payableCents: ein Sicherheitseinbehalt mindert nur den
     * Zahlbetrag, die Forderung besteht in voller Hoehe (offener Posten auf
     * dem Debitor). Mit payableCents saenke die Kachel im Abschlagsmonat um
     * den Einbehalt und spraenge im Monat der Schlussrechnung um dieselbe
     * Summe hoch — Umsatz, der nie gestellt wurde.
     *
     * Storno- und Gutschriftbelege tragen eine negative Forderung und
     * mindern den Monat. Das ist gewollt: die Kachel beantwortet, was unterm
     * Strich gefordert wurde, nicht wie viele Belege gedruckt wurden.
     */
    monthIssuedCents: Cents;
    /** Ausgestellte Belege des laufenden Monats, Korrekturbelege eingeschlossen. */
    monthIssuedCount: number;
    /** Der Vormonat zu `currentMonth`, Format YYYY-MM. */
    previousMonth: string;
    /**
     * Der Vormonat NUR BIS ZUM SELBEN TAG DES MONATS wie heute.
     *
     * Das ist die Bezugsgroesse des Trends, und die Kappung ist der Punkt: am
     * 8. eines Monats gegen einen vollen Vormonat zu vergleichen ergibt jeden
     * Monat aufs Neue einen dramatischen Einbruch, der nichts bedeutet ausser
     * dass der Monat noch laeuft. Am Monatsende faellt der Wert mit
     * `prevMonthFullCents` zusammen.
     *
     * Hat der Vormonat weniger Tage als heute Tag ist, greift die Kappung
     * nicht — es gibt keine Belege jenseits seines letzten Tages.
     */
    prevMonthIssuedCents: Cents;
    /** Der GANZE Vormonat. Nur als Zusatzangabe, nicht Bezug des Trends. */
    prevMonthFullCents: Cents;
    /**
     * Abweichung von `monthIssuedCents` gegen `prevMonthIssuedCents`, in
     * Prozent. Positiv = mehr gestellt als im Vormonat um dieselbe Zeit.
     *
     * null, wenn der Vormonat im Vergleichszeitraum bei null steht: eine
     * Steigerung gegenueber nichts hat keinen Prozentwert. Der Nenner ist der
     * BETRAG des Vormonats — ein von Stornos ins Minus gedrueckter Vormonat
     * wuerde das Vorzeichen der Abweichung sonst umdrehen.
     */
    monthDeltaPct: number | null;
    /**
     * Tag des Monats (1–31), bis zu dem `prevMonthIssuedCents` den Vormonat
     * zaehlt: der heutige Monatstag, gedeckelt auf die Laenge des Vormonats.
     * Nur fuer die Beschriftung der Kachel ("Vormonat 1.–23. Aug") — die
     * Kappung selbst rechnet ueber den Tagesvergleich.
     */
    comparedThroughDay: number;
}

/** Ein Balken der Forderungs-Altersstruktur. */
export interface AgingBucket {
    key: '0-30' | '31-60' | '61-90' | '90+';
    label: string;
    count: number;
    cents: Cents;
}

/** Ein Monatsbalken, gestapelt nach Zahlungszustand. */
export interface MonthlyInvoicePoint {
    /** Sortierschluessel, Format YYYY-MM. */
    month: string;
    /** Anzeigetext, z. B. "Jul 26". */
    label: string;
    paid: Cents;
    open: Cents;
    overdue: Cents;
}

export interface CustomerInvoiceStats {
    customerId: string;
    name: string;
    invoiceCount: number;
    revenueNetCents: Cents;
    collectedCents: Cents;
    /** Was von den Belegen DIESES Fensters heute noch offen ist. */
    openCents: Cents;
}

export interface CustomerPaymentBehaviour {
    customerId: string;
    name: string;
    /** Belege im Fenster, die bezahlt sind und ein Zahlungsdatum haben. */
    settledCount: number;
    /** Ø Tage ueber der Faelligkeit. Negativ = vorzeitig gezahlt. */
    avgPaymentDelayDays: number | null;
    /** Ø Tage von Rechnungsdatum bis Zahlung. */
    avgDaysToPayDays: number | null;
    /** Stichtagsgroesse: was von diesen Belegen heute ueberfaellig ist. */
    overdueCents: Cents;
}

// ═══════════════════════════════════════════════════════════════════════════
// Interne Helfer
// ═══════════════════════════════════════════════════════════════════════════

const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

/**
 * "2026-08" -> "2026-07". Reine Zeichenrechnung: ein Date waere hier eine
 * Zeitzonenfalle, denn der Monatswechsel ist genau der Punkt, an dem eine um
 * Stunden verschobene Ortszeit im falschen Monat landet.
 */
const previousMonthOf = (month: string): string => {
    const year = Number(month.slice(0, 4));
    const m = Number(month.slice(5, 7));
    const prevYear = m === 1 ? year - 1 : year;
    const prevMonth = m === 1 ? 12 : m - 1;
    return `${prevYear}-${String(prevMonth).padStart(2, '0')}`;
};

/**
 * "2026-08" -> "Aug 2026". Hier statt in der Kachel, weil die Monatsnamen
 * schon hier stehen. Unbekannte Eingaben kommen unveraendert zurueck statt
 * als "undefined". Der Monatschart schreibt bewusst kuerzer ("Aug 26") — auf
 * seiner Achse stehen bis zu 24 Beschriftungen nebeneinander.
 */
export const monthLabel = (month: string): string => {
    const name = MONTHS_SHORT[Number(month.slice(5, 7)) - 1];
    return name ? `${name} ${month.slice(0, 4)}` : month;
};

/** Tage des Monats, Format YYYY-MM. Tag 0 des Folgemonats ist der letzte Tag dieses Monats. */
const daysInMonth = (month: string): number =>
    new Date(Date.UTC(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)).getUTCDate();

/**
 * Untertitel der Kachel "Gestellt": wie viele Belege dieser Monat traegt und
 * woran er gemessen wird.
 *
 * Der Vergleichswert ist normalerweise der GEKAPPTE Vormonat, weil die
 * Prozentangabe daneben gegen genau diesen rechnet. Der Untertitel nennt die
 * Kappung als TAGESSPANNE INNERHALB des Vormonats: "Vormonat 1.–23. Aug". Die
 * fruehere Fassung "Aug 2026 bis heute 160.683 €" wurde als Zeitraum gelesen —
 * "von August bis heute" — und der Betrag widersprach dann der Monatszahl
 * darueber, die groesser war. Eine Spanne, die im Vormonat endet, laesst diese
 * Lesart nicht zu.
 *
 * Am MONATSANFANG faellt die Kappung mit dem Vormonat zusammen: am 1. bleibt
 * vom Vormonat nur sein eigener 1. uebrig, der gekappte Wert steht bei null,
 * und die Kachel schrieb "Aug 2026 0,00 EUR" — als haette der Vormonat nichts
 * gebracht. Es gibt in diesem Fall auch keine Prozentangabe (monthDeltaPct ist
 * null), also gibt es nichts, dem der Untertitel widersprechen koennte: dann
 * nennt er den GANZEN Vormonat und sagt das auch. Dasselbe, wenn der
 * Vergleichstag den Vormonat ohnehin abdeckt (30. Maerz gegen einen Februar
 * mit 28 Tagen): "1.–28. Feb" waere richtig, "gesamt" ist die ehrlichere Zahl.
 *
 * Traegt der laufende Monat NOCH KEINEN Beleg, faellt der vordere Teil ganz
 * weg. "noch nichts" sagt dasselbe wie die 0,00 EUR direkt darueber.
 *
 * Rein textbildend, keine Rechnung — die Zahlen kommen unveraendert aus stats.
 */
export const monthComparisonSub = (
    stats: Pick<
        InvoiceStats,
        | 'monthIssuedCount'
        | 'previousMonth'
        | 'prevMonthIssuedCents'
        | 'prevMonthFullCents'
        | 'comparedThroughDay'
    >,
    formatMoney: (cents: Cents) => string,
): string => {
    const belege = (n: number) => `${n} ${n === 1 ? 'Beleg' : 'Belege'}`;
    const prevLabel = monthLabel(stats.previousMonth);
    // Leerer Monat: kein Vorspann, der Vormonat bekommt die ganze Zeile.
    const lead = stats.monthIssuedCount === 0 ? '' : `${belege(stats.monthIssuedCount)} · `;

    if (stats.prevMonthIssuedCents === 0 && stats.prevMonthFullCents === 0) {
        return stats.monthIssuedCount === 0
            ? 'diesen Monat noch nichts'
            : `${lead}kein Vormonatswert`;
    }
    // Kappung hat den Vergleichszeitraum leergeraeumt oder deckt ihn ganz ab:
    // ganzer Vormonat.
    const coversWholeMonth = stats.comparedThroughDay >= daysInMonth(stats.previousMonth);
    if (stats.prevMonthIssuedCents === 0 || coversWholeMonth) {
        return `${lead}Vormonat ${prevLabel} gesamt: ${formatMoney(stats.prevMonthFullCents)}`;
    }
    const monthName = MONTHS_SHORT[Number(stats.previousMonth.slice(5, 7)) - 1] ?? stats.previousMonth;
    const span = stats.comparedThroughDay === 1 ? '1.' : `1.–${stats.comparedThroughDay}.`;
    return `${lead}Vormonat ${span} ${monthName}: ${formatMoney(stats.prevMonthIssuedCents)}`;
};

/** Maximale Balkenzahl im Monatschart — ohne Deckel spannt "Alle" beliebig weit. */
const MAX_MONTHS = 24;

const day = (iso?: string | null): string | undefined => (iso ? iso.slice(0, 10) : undefined);

const pct = (part: number, total: number): number | null =>
    total > 0 ? (part / total) * 100 : null;

const inWindow = (date: string | undefined, w: DateWindow): boolean => {
    if (!date) return false;
    if (w.from && date < w.from) return false;
    if (w.to && date > w.to) return false;
    return true;
};

const daysBetween = (fromIso: string, toIso: string): number =>
    Math.round(
        (Date.parse(`${toIso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${fromIso.slice(0, 10)}T00:00:00Z`)) / 86_400_000,
    );

const avg = (sum: number, count: number): number | null => (count > 0 ? sum / count : null);

/** Status, in denen der Beleg noch gar nicht existiert hat — kein Umsatz, keine Forderung. */
const NOT_YET_ISSUED: InvoiceStatus[] = ['DRAFT', 'READY_FOR_REVIEW', 'APPROVED', 'ISSUING'];

/**
 * Ob der Beleg jemals ausgestellt wurde.
 *
 * CANCELLED ist der einzige mehrdeutige Status: ihn tragen sowohl der
 * weggeworfene Entwurf als auch die ausgestellte und spaeter stornierte
 * Rechnung. Auseinanderhalten laesst sie nur die Nummer, die es erst ab ISSUED
 * gibt. Ohne diese Unterscheidung wuerde jeder verworfene Entwurf als
 * Umsatzminderung in der Storno-Quote landen.
 */
export const isIssuedInvoice = (invoice: Pick<Invoice, 'status' | 'invoiceNumber'>): boolean => {
    if (NOT_YET_ISSUED.includes(invoice.status)) return false;
    if (invoice.status === 'CANCELLED') return !!invoice.invoiceNumber;
    return true;
};

/**
 * Effektive Status, in denen der Beleg eine Forderung darstellt.
 *
 * DISPUTED ist dabei: eine bestrittene Rechnung ist umstritten, aber sie ist
 * gebucht und noch nicht bezahlt. Wer sie ausblendet, meldet eine zu niedrige
 * Forderungssumme und uebersieht genau die Faelle, die Aufmerksamkeit brauchen.
 */
// PARTIALLY_CREDITED gehoert dazu: eine teilweise gutgeschriebene Rechnung ist
// weiterhin offen. Fehlte der Status, waere ihr Restbetrag im Cockpit
// unsichtbar — die Forderung existierte, taeuchte aber weder in den offenen
// Forderungen noch in der Altersstruktur auf.
const OWING_STATUSES: InvoiceStatus[] = [
    'ISSUED',
    'SENT',
    'PARTIALLY_PAID',
    'PARTIALLY_CREDITED',
    'OVERDUE',
    'DISPUTED',
];

const isOwing = (invoice: Invoice, todayIso: string): boolean =>
    isIssuedInvoice(invoice) && OWING_STATUSES.includes(effectiveInvoiceStatus(invoice, todayIso));

/**
 * Mindert der Beleg den Umsatz?
 *
 * NICHT ueber CORRECTION_TYPES: diese Menge dient in invoiceLogic der
 * Validierung (negative Mengen zulassen) und enthaelt deshalb auch
 * SELF_BILLING_CREDIT_NOTE. Die Gutschrift im umsatzsteuerlichen Sinn ist aber
 * eine Abrechnung des Kunden ueber UNSERE Leistung (§14 Abs. 2 Satz 2 UStG) —
 * ein positiver Umsatz. Sie in der Stornoquote zu fuehren, waere falsch.
 * Massgeblich ist NEGATIVE_DOCUMENT_TYPES aus creditNoteLogic, wo genau diese
 * Unterscheidung getroffen und getestet ist.
 */
const isCorrection = (type: DocumentType): boolean => NEGATIVE_DOCUMENT_TYPES.has(type);

/**
 * Umsatzbeitrag eines Belegs, netto.
 *
 * VORZEICHEN — die zentrale Festlegung dieses Moduls:
 *
 * 1. Jeder ausgestellte Beleg zaehlt mit dem Vorzeichen, das in seinen Summen
 *    steht. Storno- und Gutschriftbelege werden im Modul mit negativen Mengen
 *    erfasst (siehe validateInvoice: negative Mengen sind nur dort erlaubt),
 *    tragen also von sich aus ein negatives Netto und mindern den Umsatz.
 *
 * 2. Der stornierte URSPRUNGSBELEG bleibt mit seinem positiven Umsatz drin,
 *    obwohl sein Status auf CANCELLED steht. Genau hier liegt die Falle: wer
 *    ihn zusaetzlich ausblendet, laesst denselben Storno den Umsatz ZWEIMAL
 *    mindern — einmal durch das Weglassen des Originals, einmal durch den
 *    negativen Stornobeleg. Original plus Storno ergeben in Summe null, und das
 *    ist das richtige Ergebnis.
 *
 * 3. Abgezogene Abschlaege werden abgesetzt. Die Schlussrechnung weist die
 *    GESAMTleistung aus und zieht die Abschlaege erst vom Brutto ab
 *    (calculateInvoiceTotals). Ohne diese Korrektur wuerde die bereits mit der
 *    Abschlagsrechnung vereinnahmte Leistung ein zweites Mal als Umsatz
 *    erscheinen.
 */
export const revenueNetCents = (invoice: Invoice): Cents =>
    invoice.totals.netTotalCents - sumCents(invoice.advanceDeductions.map((d) => d.netCents));

/**
 * Was dieser Beleg BRUTTO gefordert hat: Bruttobetrag abzueglich der
 * vereinnahmten Abschlaege.
 *
 * Bewusst NICHT payableCents, und daran haengt der Sicherheitseinbehalt: er
 * mindert allein den Zahlbetrag, waehrend die Forderung in voller Hoehe
 * bestehen bleibt (der Einbehalt ist ein offener Posten auf dem Debitor, keine
 * Entgeltminderung — siehe docs/sicherheitseinbehalt-plan.md §2). Mit
 * payableCents saenke jede Monatszahl im Abschlagsmonat um den Einbehalt und
 * spraenge im Monat der Schlussrechnung um dieselbe Summe hoch, obwohl in
 * keinem der beiden Monate etwas anderes gefordert wurde.
 *
 * Auf einem Beleg ohne Einbehalt ist der Wert zeichengleich zu payableCents.
 */
export const issuedClaimCents = (invoice: Invoice): Cents =>
    invoice.totals.grossTotalCents - invoice.totals.prepaidCents;

/**
 * Letzter Zahlungseingang je Beleg.
 *
 * Nur INCOMING: eine Erstattung ist kein Zahlungsverhalten des Kunden und
 * wuerde die Zahlungsdauer sonst kuenstlich verlaengern.
 */
const lastIncomingPaymentByInvoice = (payments: InvoicePayment[]): Map<string, string> => {
    const out = new Map<string, string>();
    for (const p of payments) {
        if (p.direction !== 'INCOMING') continue;
        const date = day(p.valueDate);
        if (!date) continue;
        const current = out.get(p.invoiceId);
        if (!current || date > current) out.set(p.invoiceId, date);
    }
    return out;
};

/** Ein Beleg gilt als erledigt, wenn nichts mehr offen ist und Geld geflossen ist. */
const isSettled = (invoice: Invoice): boolean =>
    isIssuedInvoice(invoice) && invoice.paidCents > 0 && openAmountCents(invoice) <= 0;

// ═══════════════════════════════════════════════════════════════════════════
// Hauptkennzahlen
// ═══════════════════════════════════════════════════════════════════════════

export const computeInvoiceStats = (
    invoices: Invoice[],
    payments: InvoicePayment[],
    opts: { today: string } & DateWindow,
): InvoiceStats => {
    const today = opts.today.slice(0, 10);
    const w: DateWindow = { from: opts.from, to: opts.to };
    const lastPayment = lastIncomingPaymentByInvoice(payments);

    let openCents = 0, openCount = 0;
    let overdueCents = 0, overdueCount = 0;
    let draftCount = 0, inReviewCount = 0;
    let customerCreditCents = 0;

    let issuedCount = 0;
    let revenue = 0;
    let collectedCents = 0;
    let creditedCount = 0, creditedNetCents = 0;
    let grossIssuedNet = 0;
    let dsoSum = 0, dsoCount = 0;
    let delaySum = 0, delayCount = 0;
    const currentMonth = today.slice(0, 7);
    const previousMonth = previousMonthOf(currentMonth);
    const dayOfMonth = today.slice(8, 10);
    // Hat der Vormonat weniger Tage als heute Tag ist, endet der Vergleich
    // mit seinem letzten Tag — es gibt keine Belege jenseits davon.
    const comparedThroughDay = Math.min(Number(dayOfMonth), daysInMonth(previousMonth));
    let monthIssuedCents = 0, monthIssuedCount = 0;
    let prevMonthIssuedCents = 0, prevMonthFullCents = 0;

    for (const invoice of invoices) {
        // ── Stichtag ────────────────────────────────────────────────────────
        if (invoice.status === 'DRAFT') draftCount += 1;
        if (invoice.status === 'READY_FOR_REVIEW') inReviewCount += 1;

        if (isIssuedInvoice(invoice)) {
            // Ueberzahlungen sind nie eine negative Forderung, sondern Guthaben
            // (Spezifikation §20). "Unverbraucht" heisst: der Ueberhang steht
            // noch auf seinem Beleg — eine Verrechnung erscheint auf dem
            // Zielbeleg als Zahlung und mindert ihn dort.
            //
            // Bezugsgroesse bleibt der ZAHLBETRAG: ueberzahlt hat, wer mehr
            // ueberweist als gefordert war, und gefordert war bei einem
            // Einbehalt eben nur der geminderte Betrag. Mit gross − prepaid
            // bliebe genau der Einbehalt als stilles "Guthaben" unsichtbar.
            customerCreditCents += overpaymentCents(
                invoice.totals.payableCents,
                invoice.paidCents,
                invoice.creditedCents,
            );
        }

        if (isOwing(invoice, today)) {
            // Bewusst openAmountCents statt invoice.openCents: die generierte
            // Datenbankspalte wird bei Ueberzahlung negativ und wuerde die
            // Summe ueber andere Belege hinweg stillschweigend druecken.
            const open = openAmountCents(invoice);
            if (open > 0) {
                openCents += open;
                openCount += 1;
                if (effectiveInvoiceStatus(invoice, today) === 'OVERDUE') {
                    overdueCents += open;
                    overdueCount += 1;
                }
            }
        }

        // ── Zeitraum ────────────────────────────────────────────────────────
        if (!isIssuedInvoice(invoice)) continue;
        const invoiceDate = day(invoice.invoiceDate);

        // Der laufende Monat wird VOR dem Fensterfilter gezaehlt. Er haengt am
        // Stichtag, nicht am eingestellten Zeitraum — ein Fenster, das heute
        // nicht einschliesst, duerfte die Monatszahl nicht auf null druecken.
        // "Gestellt" ist die Forderung (gross − prepaid), nicht der Zahlbetrag:
        // Begruendung an monthIssuedCents. Alle drei Monatswerte muessen
        // dieselbe Groesse zaehlen, sonst vergleicht monthComparisonSub
        // Forderung mit Zahlbetrag.
        const invoiceMonth = invoiceDate?.slice(0, 7);
        const claim = issuedClaimCents(invoice);
        if (invoiceMonth === currentMonth) {
            monthIssuedCents += claim;
            monthIssuedCount += 1;
        } else if (invoiceMonth === previousMonth && invoiceDate) {
            prevMonthFullCents += claim;
            // Zeichenvergleich auf zweistelligen Tagen — "09" <= "26" gilt.
            if (invoiceDate.slice(8, 10) <= dayOfMonth) {
                prevMonthIssuedCents += claim;
            }
        }

        if (!inWindow(invoiceDate, w)) continue;

        issuedCount += 1;
        const net = revenueNetCents(invoice);
        revenue += net;
        collectedCents += invoice.paidCents;

        if (isCorrection(invoice.documentType)) {
            creditedCount += 1;
            // Positiv ausgewiesen, damit die Kachel "Gutschriften: 1.200 €"
            // zeigt. Vom Umsatz abgezogen ist der Betrag bereits ueber das
            // Vorzeichen des Belegs — hier NICHT ein zweites Mal.
            creditedNetCents += Math.abs(net);
        } else {
            grossIssuedNet += net;
        }

        const paidOn = lastPayment.get(invoice.id);
        if (paidOn && isSettled(invoice) && invoiceDate) {
            dsoSum += Math.max(0, daysBetween(invoiceDate, paidOn));
            dsoCount += 1;
        }
        if (paidOn && isSettled(invoice) && invoice.dueDate) {
            // Nicht bei 0 gekappt: wer zehn Tage vor Faelligkeit zahlt, soll
            // sich vom puenktlichen Zahler unterscheiden.
            delaySum += daysBetween(invoice.dueDate, paidOn);
            delayCount += 1;
        }
    }

    return {
        openCents,
        openCount,
        overdueCents,
        overdueCount,
        draftCount,
        inReviewCount,
        customerCreditCents,
        issuedCount,
        revenueNetCents: revenue,
        collectedCents,
        creditedCount,
        creditedNetCents,
        cancellationRatePct: pct(creditedNetCents, grossIssuedNet),
        dsoDays: avg(dsoSum, dsoCount),
        avgPaymentDelayDays: avg(delaySum, delayCount),
        currentMonth,
        monthIssuedCents,
        monthIssuedCount,
        previousMonth,
        prevMonthIssuedCents,
        prevMonthFullCents,
        monthDeltaPct:
            prevMonthIssuedCents === 0
                ? null
                : ((monthIssuedCents - prevMonthIssuedCents) / Math.abs(prevMonthIssuedCents)) * 100,
        comparedThroughDay,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Altersstruktur
// ═══════════════════════════════════════════════════════════════════════════

const AGING_DEFS: Array<{ key: AgingBucket['key']; label: string; maxDays: number }> = [
    { key: '0-30', label: '0–30 Tage', maxDays: 30 },
    { key: '31-60', label: '31–60 Tage', maxDays: 60 },
    { key: '61-90', label: '61–90 Tage', maxDays: 90 },
    { key: '90+', label: 'über 90 Tage', maxDays: Number.POSITIVE_INFINITY },
];

/**
 * Altersstruktur der offenen Forderungen — reine Stichtagsgroesse, ohne
 * Zeitfenster. Gestaffelt nach Tagen ueber der Faelligkeit.
 *
 * Noch nicht faellige Forderungen stehen im ersten Balken (0 Tage Verzug): sie
 * sind eine Forderung, aber kein Mahnfall, und ein eigener Balken dafuer wuerde
 * die vier ueblichen Altersklassen der Offenen-Posten-Liste sprengen.
 *
 * Es kommen immer alle vier Balken zurueck, auch leere — ein Chart, dessen
 * Achse je nach Datenlage die Klassen wechselt, ist nicht vergleichbar.
 */
export const receivablesAging = (
    invoices: Invoice[],
    opts: { today: string },
): AgingBucket[] => {
    const today = opts.today.slice(0, 10);
    const buckets: AgingBucket[] = AGING_DEFS.map((d) => ({
        key: d.key,
        label: d.label,
        count: 0,
        cents: 0,
    }));

    for (const invoice of invoices) {
        if (!isOwing(invoice, today)) continue;
        const open = openAmountCents(invoice);
        if (open <= 0) continue;

        // daysPastDue statt daysOverdue: die Altersstruktur fragt, wie ALT eine
        // Forderung ist, nicht ob sie gemahnt werden darf. Ein bestrittener
        // Beleg wird bewusst nicht gemahnt, ist aber trotzdem seit Monaten
        // faellig — ueber daysOverdue landete er im Balken "0-30 Tage".
        const overdue = daysPastDue(invoice, today);
        const index = AGING_DEFS.findIndex((d) => overdue <= d.maxDays);
        const bucket = buckets[index >= 0 ? index : buckets.length - 1];
        bucket.count += 1;
        bucket.cents += open;
    }

    return buckets;
};

// ═══════════════════════════════════════════════════════════════════════════
// Monatsverlauf
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Rechnungsvolumen je Monat, verankert am Rechnungsdatum, gestapelt nach
 * heutigem Zahlungszustand.
 *
 * Anders als der Umsatz rechnet dieser Chart mit dem ZAHLBETRAG (brutto): ein
 * Nettobetrag laesst sich nicht nach Zahlungszustand aufteilen, weil Zahlungen
 * brutto eingehen. Die drei Segmente addieren sich konstruktionsbedingt zum
 * Zahlbetrag des Monats.
 *
 * Ohne Fenstergrenzen wird die Spanne aus den Daten abgeleitet und auf die
 * letzten MAX_MONTHS Monate begrenzt.
 */
export const monthlyInvoiceVolume = (
    invoices: Invoice[],
    opts: { today: string } & DateWindow,
): MonthlyInvoicePoint[] => {
    const today = opts.today.slice(0, 10);
    const w: DateWindow = { from: opts.from, to: opts.to };

    const buckets = new Map<string, MonthlyInvoicePoint>();
    for (const invoice of invoices) {
        if (!isIssuedInvoice(invoice)) continue;
        const date = day(invoice.invoiceDate);
        if (!date || !inWindow(date, w)) continue;

        const month = date.slice(0, 7);
        let point = buckets.get(month);
        if (!point) {
            const monthIndex = Number(month.slice(5, 7)) - 1;
            point = {
                month,
                label: `${MONTHS_SHORT[monthIndex] ?? month.slice(5, 7)} ${month.slice(2, 4)}`,
                paid: 0,
                open: 0,
                overdue: 0,
            };
            buckets.set(month, point);
        }

        const open = openAmountCents(invoice);
        // Der beglichene Teil ist der Rest zum Zahlbetrag, nicht paidCents:
        // Gutschriften begleichen ebenfalls, und bei Ueberzahlung waere
        // paidCents groesser als der Beleg. Bei einem Stornobeleg ist der
        // Zahlbetrag negativ — der Balken des Monats sinkt dann, was gewollt ist.
        //
        // Hier bleibt es beim ZAHLBETRAG (payableCents), anders als bei
        // monthIssuedCents: die drei Segmente beschreiben den Zahlungszustand,
        // und "bezahlt" kann ein Beleg nur bis zu dem Betrag sein, den der
        // Kunde tatsaechlich schuldet — der Einbehalt ist weder bezahlt noch
        // offen, sondern zurueckgehalten, und erscheint erst mit der
        // Schlussrechnung, die ihn fordert.
        point.paid += invoice.totals.payableCents - open;
        if (open > 0) {
            if (effectiveInvoiceStatus(invoice, today) === 'OVERDUE') point.overdue += open;
            else point.open += open;
        }
    }

    return [...buckets.values()].sort((a, b) => a.month.localeCompare(b.month)).slice(-MAX_MONTHS);
};

// ═══════════════════════════════════════════════════════════════════════════
// Kundenauswertungen
// ═══════════════════════════════════════════════════════════════════════════

const customerName = (invoice: Invoice): string =>
    invoice.customerName || invoice.customerSnapshot?.name || '—';

/**
 * Kunden nach Nettoumsatz im Fenster (Anker: Rechnungsdatum).
 *
 * openCents ist hier bewusst auf die Belege DIESES Fensters beschraenkt und
 * damit keine Stichtagsgroesse im Sinne von computeInvoiceStats — die Spalte
 * beantwortet "was steht aus dem Umsatz dieses Zeitraums noch aus".
 */
export const topCustomersByRevenue = (
    invoices: Invoice[],
    opts: { today: string; limit?: number } & DateWindow,
): CustomerInvoiceStats[] => {
    const limit = opts.limit ?? 10;
    const w: DateWindow = { from: opts.from, to: opts.to };

    const acc = new Map<string, CustomerInvoiceStats>();
    for (const invoice of invoices) {
        if (!isIssuedInvoice(invoice)) continue;
        if (!inWindow(day(invoice.invoiceDate), w)) continue;

        const key = invoice.customerId || 'unknown';
        let row = acc.get(key);
        if (!row) {
            row = {
                customerId: key,
                name: customerName(invoice),
                invoiceCount: 0,
                revenueNetCents: 0,
                collectedCents: 0,
                openCents: 0,
            };
            acc.set(key, row);
        }
        // Der Kundenname kommt aus einem Join und fehlt nicht auf jedem Beleg.
        if (row.name === '—') row.name = customerName(invoice);

        row.invoiceCount += 1;
        // Umsatz netto (vom Einbehalt unberuehrt — er ist keine
        // Entgeltminderung), offen ueber openAmountCents und damit auf dem
        // Zahlbetrag: dieselbe Trennung wie in computeInvoiceStats.
        row.revenueNetCents += revenueNetCents(invoice);
        row.collectedCents += invoice.paidCents;
        row.openCents += openAmountCents(invoice);
    }

    return [...acc.values()]
        .sort((a, b) => b.revenueNetCents - a.revenueNetCents || b.invoiceCount - a.invoiceCount)
        .slice(0, limit);
};

/**
 * Zahlungsmoral je Kunde: wie lange nach Faelligkeit tatsaechlich gezahlt wird.
 *
 * Getrennt von topCustomersByRevenue, weil beide Listen unterschiedliche Fragen
 * beantworten und unterschiedlich sortiert sind — der groesste Kunde ist nicht
 * automatisch der saeumigste, und genau diese Kombination ist die interessante.
 *
 * Kunden ohne einen einzigen abgeschlossenen Zahlungsvorgang im Fenster fallen
 * heraus: ein Durchschnitt aus null Werten ist keine Aussage.
 */
export const customersByPaymentDelay = (
    invoices: Invoice[],
    payments: InvoicePayment[],
    opts: { today: string; limit?: number } & DateWindow,
): CustomerPaymentBehaviour[] => {
    const today = opts.today.slice(0, 10);
    const limit = opts.limit ?? 10;
    const w: DateWindow = { from: opts.from, to: opts.to };
    const lastPayment = lastIncomingPaymentByInvoice(payments);

    interface Acc extends CustomerPaymentBehaviour {
        delaySum: number;
        toPaySum: number;
    }
    const acc = new Map<string, Acc>();

    for (const invoice of invoices) {
        if (!isIssuedInvoice(invoice)) continue;
        const invoiceDate = day(invoice.invoiceDate);
        if (!inWindow(invoiceDate, w)) continue;

        const key = invoice.customerId || 'unknown';
        let row = acc.get(key);
        if (!row) {
            row = {
                customerId: key,
                name: customerName(invoice),
                settledCount: 0,
                avgPaymentDelayDays: null,
                avgDaysToPayDays: null,
                overdueCents: 0,
                delaySum: 0,
                toPaySum: 0,
            };
            acc.set(key, row);
        }
        if (row.name === '—') row.name = customerName(invoice);

        if (isOwing(invoice, today) && effectiveInvoiceStatus(invoice, today) === 'OVERDUE') {
            row.overdueCents += openAmountCents(invoice);
        }

        const paidOn = lastPayment.get(invoice.id);
        if (!paidOn || !isSettled(invoice) || !invoiceDate || !invoice.dueDate) continue;
        row.settledCount += 1;
        row.delaySum += daysBetween(invoice.dueDate, paidOn);
        row.toPaySum += Math.max(0, daysBetween(invoiceDate, paidOn));
    }

    return [...acc.values()]
        .filter((row) => row.settledCount > 0)
        .map(({ delaySum, toPaySum, ...row }) => ({
            ...row,
            avgPaymentDelayDays: avg(delaySum, row.settledCount),
            avgDaysToPayDays: avg(toPaySum, row.settledCount),
        }))
        .sort((a, b) => (b.avgPaymentDelayDays ?? 0) - (a.avgPaymentDelayDays ?? 0))
        .slice(0, limit);
};

// ═══════════════════════════════════════════════════════════════════════════
// Gestellt je Zeitraum
// ═══════════════════════════════════════════════════════════════════════════

export type IssuedGranularity = 'month' | 'week' | 'day';

/** Eine Zeile der Aufschluesselung "Gestellt je Monat / Woche / Tag". */
export interface IssuedPeriodPoint {
    /** Sortierschluessel: YYYY-MM, YYYY-Www (ISO-Woche) oder YYYY-MM-DD. */
    key: string;
    /** Anzeigetext: "Sep 2026", "KW 39 · 21.–27.09.2026", "Mi, 23.09.2026". */
    label: string;
    /** Erster und letzter Kalendertag des Zeitraums (YYYY-MM-DD). */
    from: string;
    to: string;
    /** Gestellte Forderung, brutto (gross − prepaid); Korrekturbelege mindern. */
    cents: Cents;
    /** Ausgestellte Belege, Korrekturbelege eingeschlossen. */
    count: number;
    /** Davon Storno- und Gutschriftbelege. */
    correctionCount: number;
    /** Der Zeitraum enthaelt `today` — er ist noch nicht abgeschlossen. */
    running: boolean;
}

/** Hoechstzahl Zeilen je Koernung — ohne Deckel listete "Tag" jeden Tag seit dem ersten Beleg. */
const ISSUED_ROW_LIMIT: Record<IssuedGranularity, number> = { month: 24, week: 26, day: 60 };

const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const utcDate = (iso: string): Date => new Date(`${iso}T00:00:00Z`);
const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
const shiftDays = (iso: string, days: number): string => {
    const d = utcDate(iso);
    d.setUTCDate(d.getUTCDate() + days);
    return isoDay(d);
};
/** "2026-09-23" -> "23.09.2026" */
const dottedDate = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/**
 * ISO-Kalenderwoche (Mo–So) zu einem Datum. Eigene UTC-Rechnung statt
 * dateService.getCalendarWeek: dieses Modul arbeitet ausschliesslich auf
 * ISO-Strings und darf nicht von der lokalen Zeitzone abhaengen — die
 * Kennzahlen muessen auf jedem Rechner dieselben sein.
 *
 * Das ISO-Jahr ist das Jahr des Donnerstags der Woche: der 29.12.2025 liegt in
 * KW 1 des Jahres 2026.
 */
const isoWeekOf = (iso: string): { year: number; week: number; monday: string; sunday: string } => {
    const dow = utcDate(iso).getUTCDay() || 7; // Mo = 1 … So = 7
    const monday = shiftDays(iso, 1 - dow);
    const thursday = utcDate(shiftDays(monday, 3));
    const year = thursday.getUTCFullYear();
    const week = Math.ceil(((thursday.getTime() - Date.UTC(year, 0, 1)) / 86_400_000 + 1) / 7);
    return { year, week, monday, sunday: shiftDays(monday, 6) };
};

/**
 * "21.–27.09.2026"; ueber die Monatsgrenze "28.09.–04.10.2026"; ueber die
 * Jahresgrenze "29.12.2025–04.01.2026". Weggelassen wird nur, was beide
 * Enden gemeinsam haben.
 */
const spanLabel = (from: string, to: string): string => {
    if (from.slice(0, 4) !== to.slice(0, 4)) return `${dottedDate(from)}–${dottedDate(to)}`;
    if (from.slice(0, 7) !== to.slice(0, 7)) return `${from.slice(8, 10)}.${from.slice(5, 7)}.–${dottedDate(to)}`;
    return `${from.slice(8, 10)}.–${dottedDate(to)}`;
};

/** Schluessel, Beschriftung und Grenzen des Zeitraums, in dem `iso` liegt. */
const periodOf = (
    iso: string,
    granularity: IssuedGranularity,
): Pick<IssuedPeriodPoint, 'key' | 'label' | 'from' | 'to'> => {
    if (granularity === 'month') {
        const month = iso.slice(0, 7);
        return { key: month, label: monthLabel(month), from: `${month}-01`, to: `${month}-${daysInMonth(month)}` };
    }
    if (granularity === 'week') {
        const { year, week, monday, sunday } = isoWeekOf(iso);
        return {
            key: `${year}-W${String(week).padStart(2, '0')}`,
            label: `KW ${week} · ${spanLabel(monday, sunday)}`,
            from: monday,
            to: sunday,
        };
    }
    return {
        key: iso,
        label: `${WEEKDAYS_SHORT[utcDate(iso).getUTCDay()]}, ${dottedDate(iso)}`,
        from: iso,
        to: iso,
    };
};

/**
 * Was je Monat, Kalenderwoche oder Tag GESTELLT wurde — dieselbe Groesse wie
 * die Kachel "Gestellt" (issuedClaimCents: brutto, Abschlaege abgesetzt), am
 * Rechnungsdatum verankert. Die Zeile des laufenden Monats ist deshalb
 * zeichengleich mit der Kachel; wer beides nebeneinander sieht, darf keine
 * Differenz finden.
 *
 * Bewusst NICHT der Zahlbetrag wie im Monatschart: dort geht es um den
 * Zahlungszustand, hier um die Frage "wie viel habe ich geschrieben". Der
 * Sicherheitseinbehalt drueckte die Zeile sonst im Abschlagsmonat und hoebe
 * sie im Monat der Schlussrechnung, ohne dass etwas anderes gestellt wurde.
 *
 * Es erscheinen nur Zeitraeume mit mindestens einem Beleg, neueste zuerst,
 * gedeckelt nach ISSUED_ROW_LIMIT (oder `limit`). Leere Tage aufzulisten
 * waere Rauschen, und fuer Monate gilt dasselbe Prinzip — eine Liste, die je
 * nach Koernung mal Luecken zeigt und mal nicht, liest sich als zwei
 * verschiedene Auswertungen.
 *
 * Kein Zeitfenster: die Frage "was habe ich im Juli geschrieben" haengt nicht
 * am Zeitraumfilter des Cockpits. Der Deckel ist das Fenster.
 */
export const issuedByPeriod = (
    invoices: Invoice[],
    opts: { today: string; granularity: IssuedGranularity; limit?: number },
): IssuedPeriodPoint[] => {
    const today = opts.today.slice(0, 10);
    const runningKey = periodOf(today, opts.granularity).key;
    const limit = opts.limit ?? ISSUED_ROW_LIMIT[opts.granularity];

    const buckets = new Map<string, IssuedPeriodPoint>();
    for (const invoice of invoices) {
        if (!isIssuedInvoice(invoice)) continue;
        const date = day(invoice.invoiceDate);
        if (!date) continue;

        const period = periodOf(date, opts.granularity);
        let point = buckets.get(period.key);
        if (!point) {
            point = { ...period, cents: 0, count: 0, correctionCount: 0, running: period.key === runningKey };
            buckets.set(period.key, point);
        }
        point.cents += issuedClaimCents(invoice);
        point.count += 1;
        if (isCorrection(invoice.documentType)) point.correctionCount += 1;
    }

    return [...buckets.values()].sort((a, b) => b.key.localeCompare(a.key)).slice(0, limit);
};
